/**
 * @bos/portal3 — the account page (route `/account`, inside <AppShell>).
 *
 * View the profile (email read-only, role + member-since display-only) and two edit flows
 * that go through AuthContext:
 *   - Display name → `account.updateDisplayName`; the returned SafeUser updates the shared
 *     auth state, so the header greeting changes at once and survives a refresh.
 *   - Change password → `account.changePassword` with a client-side confirm-match check;
 *     the current session stays valid (only OTHER sessions are revoked server-side), so
 *     the user is NOT signed out. The wrong-current-password case surfaces cleanly.
 */
import { useState, type FormEvent } from 'react';
import { PageHeader } from '../components/PageHeader';
import { ModelChoice } from '../account/ModelChoice';
import { NotificationSettings } from '../account/NotificationSettings';
import { useAuth, authErrorMessage } from '../auth/AuthContext';

/** Render an ISO timestamp as a plain, locale-formatted date (falls back to the raw string). */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function AccountPage() {
  const { user, updateDisplayName, changePassword } = useAuth();

  // ── display-name form ──
  const [name, setName] = useState(user?.displayName ?? '');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);

  // ── change-password form ──
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);

  // AppShell guarantees a user; this satisfies the type and never renders in practice.
  if (!user) return null;

  const nameUnchanged = name.trim() === user.displayName;

  async function onSaveName(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setNameError(null);
    setNameSaved(false);
    setNameBusy(true);
    try {
      await updateDisplayName(name);
      setNameSaved(true);
    } catch (err) {
      setNameError(authErrorMessage(err));
    } finally {
      setNameBusy(false);
    }
  }

  async function onChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPwError(null);
    setPwSaved(false);
    if (newPassword !== confirmPassword) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPwSaved(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(authErrorMessage(err));
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <section className="page--account">
      <PageHeader
        eyebrow="Account"
        title="Your account"
        lede="Your profile and sign-in details."
      />

      <div className="stack">
        <div className="card">
          <h2 className="card__title">Profile</h2>
          <dl className="deflist" style={{ marginTop: '1.1rem' }}>
            <div className="deflist__row">
              <dt className="deflist__key">Email</dt>
              <dd className="deflist__val">{user.email}</dd>
            </div>
            <div className="deflist__row">
              <dt className="deflist__key">Role</dt>
              <dd className="deflist__val deflist__val--muted">{user.role}</dd>
            </div>
            <div className="deflist__row">
              <dt className="deflist__key">Member since</dt>
              <dd className="deflist__val deflist__val--muted">{formatDate(user.createdAt)}</dd>
            </div>
          </dl>
        </div>

        <div className="card">
          <h2 className="card__title">Display name</h2>
          <p className="card__intro">The name shown across Sorvel.</p>
          <form className="form" onSubmit={onSaveName} aria-label="Display name" style={{ marginTop: '1.1rem' }}>
            {nameError && (
              <p className="form__error" role="alert">
                {nameError}
              </p>
            )}
            {nameSaved && (
              <p className="form__success" role="status">
                Display name updated.
              </p>
            )}
            <label className="form__field">
              <span className="form__label">Display name</span>
              <input
                className="form__input"
                type="text"
                name="displayName"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameSaved(false);
                }}
                disabled={nameBusy}
              />
            </label>
            <div className="form__actions">
              <button
                className="btn btn--primary"
                type="submit"
                disabled={nameBusy || nameUnchanged || name.trim() === ''}
              >
                {nameBusy ? 'Saving…' : 'Save display name'}
              </button>
            </div>
          </form>
        </div>

        <ModelChoice />

        <NotificationSettings />

        <div className="card">
          <h2 className="card__title">Change password</h2>
          <p className="card__intro">
            Signing in on other devices will need the new password.
          </p>
          <form className="form" onSubmit={onChangePassword} aria-label="Change password" style={{ marginTop: '1.1rem' }}>
            {pwError && (
              <p className="form__error" role="alert">
                {pwError}
              </p>
            )}
            {pwSaved && (
              <p className="form__success" role="status">
                Password changed. Other devices have been signed out.
              </p>
            )}
            <label className="form__field">
              <span className="form__label">Current password</span>
              <input
                className="form__input"
                type="password"
                name="currentPassword"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={pwBusy}
              />
            </label>
            <label className="form__field">
              <span className="form__label">New password</span>
              <input
                className="form__input"
                type="password"
                name="newPassword"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={pwBusy}
              />
            </label>
            <label className="form__field">
              <span className="form__label">Confirm new password</span>
              <input
                className="form__input"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={pwBusy}
              />
            </label>
            <div className="form__actions">
              <button className="btn btn--primary" type="submit" disabled={pwBusy}>
                {pwBusy ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
